import React, { useState, useRef, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../api';



function FadeIn({ children, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${className}`}
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}>
      {children}
    </div>
  );
}

export default function ContactSection() {
  const [name, setName] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedTelegram = telegramUsername.trim();
    if (!name.trim() || !normalizedTelegram || !message.trim()) return;
    if (!normalizedTelegram.startsWith('@')) {
      setStatus('error');
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      await api.post(`/api/contact`, {
        name: name.trim(),
        telegram_username: normalizedTelegram,
        message: message.trim(),
      });
      setStatus('success');
      setName('');
      setTelegramUsername('');
      setMessage('');
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="max-w-lg mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-neonBlue border border-neonBlue/30 rounded-full mb-4 bg-neonBlue/5">
              Contact
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
              Get in Touch
            </h2>
            <p className="text-gray-500">Send us a message or reach out on Telegram</p>
          </div>

          <div className="glass-card rounded-2xl p-8 sm:p-10">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-400 mb-2">Your Name</label>
                <input
                  id="contact-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="contact-name-input"
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue focus:ring-1 focus:ring-neonBlue/50 outline-none transition-all"
                  required
                />
              </div>
              <div>
                <label htmlFor="contact-telegram" className="block text-sm font-medium text-gray-400 mb-2">Telegram Username</label>
                <input
                  id="contact-telegram"
                  type="text"
                  value={telegramUsername}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value || value === '@' || value.startsWith('@')) {
                      setTelegramUsername(value);
                    } else {
                      setTelegramUsername(`@${value.replace(/^@+/, '')}`);
                    }
                  }}
                  data-testid="contact-telegram-input"
                  placeholder="Enter your Telegram username"
                  className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue focus:ring-1 focus:ring-neonBlue/50 outline-none transition-all"
                  required
                />
                <p className="mt-2 text-xs text-gray-600">Use your Telegram handle and make sure it starts with @</p>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-400 mb-2">Message</label>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="contact-message-input"
                  placeholder="Type your message..."
                  rows={4}
                  className="w-full px-4 py-3 bg-darkBg border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-neonBlue focus:ring-1 focus:ring-neonBlue/50 outline-none transition-all resize-none"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full flex items-center justify-center gap-2 bg-neonBlue text-black font-bold py-3.5 rounded-full hover:bg-neonBlue/80 hover:shadow-[0_0_25px_rgba(0,240,255,0.4)] disabled:opacity-50 transition-all"
              >
                {loading ? (
                  <span className="animate-spin w-5 h-5 border-2 border-black/30 border-t-black rounded-full" />
                ) : (
                  <><Send size={16} /> Send Message</>
                )}
              </button>
            </form>

            {status === 'success' && (
              <div data-testid="contact-success" className="mt-4 flex items-center gap-2 text-green-400 text-sm bg-green-500/10 px-4 py-3 rounded-xl border border-green-500/20">
                <CheckCircle size={16} /> Message sent successfully!
              </div>
            )}
            {status === 'error' && (
              <div data-testid="contact-error" className="mt-4 flex items-center gap-2 text-red-400 text-sm bg-red-500/10 px-4 py-3 rounded-xl border border-red-500/20">
                <AlertCircle size={16} /> Please enter a Telegram username starting with @.
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm mb-2">Or reach us directly:</p>
              <a href="https://t.me/Ak0nikk" target="_blank" rel="noopener noreferrer" data-testid="contact-telegram-link"
                className="text-[#2AABEE] text-sm font-medium hover:underline">
                Contact on Telegram
              </a>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
